import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@dune/ui";
import {
	WEBHOOK_EVENT_VALUES,
	type WebhookDeliveryDto,
	type WebhookDto,
	type WebhookEvent,
} from "@dune/types";
import { Copy, PlusCircle, Send } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import PageLayoutWithTabs from "../../../component/layout/PageLayoutWithTabs";
import {
	UpsertWebhookPayload,
	useCreateWebhookMutation,
	useTriggerWebhookTestMutation,
	useUpdateWebhookMutation,
	useWebhookDeliveriesQuery,
	useWebhooksQuery,
} from "../../../api/queries/webhooks.queries";
import {
	OverviewTab,
	WebhookEventsTab,
	WebhookDeliveriesTab,
	WebhookDeliveryDetailsDialog,
	WebhookFormDialog,
} from "./components";
import { TriggerTestEventDialog } from "./components/TriggerTestEventDialog";
import {
	DELIVERIES_PAGE_SIZE,
	WEBHOOK_TABS,
	type WebhookTabValue,
} from "./constants";
import { defaultFormValues, type WebhookFormValues } from "./formSchema";

const WebhooksPage = () => {
	const { data, isLoading, error } = useWebhooksQuery();
	const createWebhook = useCreateWebhookMutation();
	const updateWebhook = useUpdateWebhookMutation();
	const triggerTestEvent = useTriggerWebhookTestMutation();
	const [activeTab, setActiveTab] = useState<WebhookTabValue>("overview");
	const [deliveriesPage, setDeliveriesPage] = useState(1);

	const [dialogOpen, setDialogOpen] = useState(false);
	const [dialogMode, setDialogMode] = useState<"create" | "edit">("create");
	const [editingWebhook, setEditingWebhook] = useState<WebhookDto | null>(null);
	const [secretInfo, setSecretInfo] = useState<{
		name: string;
		secret: string;
	} | null>(null);
	const [selectedDelivery, setSelectedDelivery] =
		useState<WebhookDeliveryDto | null>(null);
	const [isTestDialogOpen, setIsTestDialogOpen] = useState(false);

	const sortedWebhooks = useMemo(() => {
		if (!data) {
			return [];
		}

		return [...data].sort((a, b) => a.name.localeCompare(b.name));
	}, [data]);

	const deliveriesQuery = useWebhookDeliveriesQuery(
		deliveriesPage,
		DELIVERIES_PAGE_SIZE,
		{ enabled: activeTab === "deliveries" },
	);

	const handleOpenCreate = () => {
		setDialogMode("create");
		setEditingWebhook(null);
		setDialogOpen(true);
	};

	const handleOpenEdit = (webhook: WebhookDto) => {
		setDialogMode("edit");
		setEditingWebhook(webhook);
		setDialogOpen(true);
	};

	const handleSubmit = async (payload: UpsertWebhookPayload) => {
		try {
			if (dialogMode === "create") {
				const result = await createWebhook.mutateAsync(payload);
				toast.success("Webhook cree avec succes");
				setSecretInfo({ name: result.webhook.name, secret: result.secret });
			} else if (editingWebhook) {
				await updateWebhook.mutateAsync({ id: editingWebhook.id, ...payload });
				toast.success("Webhook mis a jour");
			}

			setDialogOpen(false);
			setEditingWebhook(null);
		} catch (mutationError) {
			console.error(
				"Erreur lors de l'enregistrement du webhook",
				mutationError,
			);
			toast.error("Impossible d'enregistrer le webhook");
		}
	};

	const handleTriggerTest = async (event: WebhookEvent) => {
		try {
			const result = await triggerTestEvent.mutateAsync({ event });
			if (result.deliveriesCount > 0) {
				toast.success("Évènement de test envoye", {
					description: `${result.deliveriesCount} webhook(s) notifie(s)`,
				});
			} else {
				toast("Aucun webhook n'ecoute cet évènement");
			}
			setIsTestDialogOpen(false);
		} catch (mutationError) {
			console.error("Impossible d'envoyer l'évènement de test", mutationError);
			toast.error("Impossible d'envoyer l'évènement de test");
		}
	};

	const formInitialValues: WebhookFormValues = editingWebhook
		? {
				name: editingWebhook.name,
				url: editingWebhook.url,
				description: editingWebhook.description ?? "",
				isActive: editingWebhook.isActive,
				events:
					editingWebhook.events.length > 0
						? editingWebhook.events
						: [WEBHOOK_EVENT_VALUES[0]],
			}
		: defaultFormValues;

	const isSubmitting =
		dialogMode === "create" ? createWebhook.isPending : updateWebhook.isPending;

	const tabContent: Record<WebhookTabValue, React.ReactNode> = {
		overview: (
			<OverviewTab
				isLoading={isLoading}
				error={error}
				webhooks={sortedWebhooks}
				onCreate={handleOpenCreate}
				onEdit={handleOpenEdit}
			/>
		),
		events: <WebhookEventsTab />,
		deliveries: (
			<WebhookDeliveriesTab
				deliveries={deliveriesQuery.data?.data ?? []}
				meta={deliveriesQuery.data?.meta}
				isLoading={deliveriesQuery.isLoading}
				isRefetching={deliveriesQuery.isFetching && !deliveriesQuery.isLoading}
				error={deliveriesQuery.error}
				onPageChange={(nextPage) => setDeliveriesPage(Math.max(1, nextPage))}
				onInspect={(delivery) => setSelectedDelivery(delivery)}
			/>
		),
	};

	return (
		<PageLayoutWithTabs
			title="Webhooks"
			tabs={WEBHOOK_TABS}
			activeTab={activeTab}
			onTabChange={(value) => setActiveTab(value)}
			tabContent={tabContent}
			headerActions={
				<div className="flex flex-wrap gap-3">
					<Button
						variant="outline"
						onClick={() => setIsTestDialogOpen(true)}
						disabled={triggerTestEvent.isPending}
					>
						<Send className="mr-2 h-4 w-4" />
						Envoyer un event de test
					</Button>
					<Button onClick={handleOpenCreate}>
						<PlusCircle className="mr-2 h-4 w-4" />
						Nouveau webhook
					</Button>
				</div>
			}
		>
			<WebhookFormDialog
				open={dialogOpen}
				mode={dialogMode}
				initialValues={formInitialValues}
				onClose={() => {
					setDialogOpen(false);
					setEditingWebhook(null);
				}}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
			/>

			<TriggerTestEventDialog
				open={isTestDialogOpen}
				onClose={() => setIsTestDialogOpen(false)}
				onSubmit={handleTriggerTest}
				isSubmitting={triggerTestEvent.isPending}
			/>

			<WebhookSecretDialog
				info={secretInfo}
				onClose={() => setSecretInfo(null)}
			/>

			<WebhookDeliveryDetailsDialog
				delivery={selectedDelivery}
				onClose={() => setSelectedDelivery(null)}
			/>
		</PageLayoutWithTabs>
	);
};

const WebhookSecretDialog = ({
	info,
	onClose,
}: {
	info: { name: string; secret: string } | null;
	onClose: () => void;
}) => {
	const handleCopy = async () => {
		if (!info) {
			return;
		}

		try {
			await navigator.clipboard.writeText(info.secret);
			toast.success("Secret copie dans le presse-papiers");
		} catch (copyError) {
			console.error("Impossible de copier le secret", copyError);
			toast.error("Impossible de copier le secret");
		}
	};

	return (
		<Dialog open={!!info} onOpenChange={(next) => (!next ? onClose() : null)}>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<DialogTitle>Secret du webhook</DialogTitle>
					<DialogDescription>
						Conservez ce secret dans un coffre-fort. Il ne sera plus affiche une
						fois la fenetre fermee.
					</DialogDescription>
				</DialogHeader>
				{info && (
					<div className="space-y-4">
						<div>
							<p className="text-sm text-muted-foreground">Webhook</p>
							<p className="font-medium">{info.name}</p>
						</div>
						<div className="flex items-center gap-2">
							<code className="flex-1 rounded-md bg-muted px-3 py-2 text-sm">
								{info.secret}
							</code>
							<Button variant="secondary" onClick={handleCopy}>
								<Copy className="mr-2 h-4 w-4" />
								Copier
							</Button>
						</div>
					</div>
				)}
				<DialogFooter>
					<Button onClick={onClose}>Fermer</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default WebhooksPage;
